import { Text, View } from '@/components'
import { TypeGuards } from '@codeleap/common'
import { useVideoContext } from './Immersive'

type HlsErrorData = {
  details?: string
  response?: { code?: number }
}

/**
 * True when the failure is the source not existing yet rather than a real playback error —
 * most often a video still encoding on Bunny Stream, reached right after posting or via a
 * shared link. A 4xx on the manifest/level request, or hls.js's own name for that failure,
 * both mean the same thing: there's nothing to play yet, not that something is broken.
 *
 * Mirrors mobile's `isVideoUnavailableError` (apps/mobile/src/modules/Video/Immersive/utils.ts).
 */
function isVideoUnavailableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const data = error as HlsErrorData
  const code = data.response?.code

  if (typeof code === 'number' && code >= 400 && code < 500) return true

  return /manifestLoadError|manifestLoadTimeOut|levelLoadError|levelLoadTimeOut/i.test(data.details ?? '')
}

/**
 * The three shapes a video can fail in, with the copy for each — same copy family as
 * mobile's `Error.tsx`, so the two platforms read the same. Only a stall is genuinely
 * retryable — nothing is actually wrong there, the load just hasn't finished. A video that
 * isn't ready yet or failed outright will fail the exact same way again immediately, so
 * offering "tap to retry" there is a promise the app can't keep.
 */
function getVideoErrorMessage(error: unknown, stalled: boolean) {
  if (TypeGuards.isNil(error)) {
    if (!stalled) return null

    return {
      title: 'This is taking longer than usual',
      body: 'Check your connection and try again.',
      retryable: true
    }
  }

  if (isVideoUnavailableError(error)) {
    return {
      title: `This video isn't ready yet`,
      body: `It's still being processed. Check back in a little while.`,
      retryable: false
    }
  }

  return { title: `Sorry, this video can't be played`, body: undefined, retryable: false }
}

export const VideoError = () => {
  const { errorStore, stalled, retry } = useVideoContext()

  const error = errorStore.use(s => s)
  const hasError = !TypeGuards.isNil(error) || stalled

  const message = getVideoErrorMessage(error, stalled)

  if (!hasError || !message) return null

  return (
    <View
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: 16,
        paddingRight: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        cursor: message.retryable ? 'pointer' : 'default'
      }}
      onClick={message.retryable ? retry : undefined}
    >
      {/* Same 16px-each-side gutter as mobile (theme.spacing.marginHorizontal(2)): the
          overlay above is padded, this fills what's left rather than shrinking to content. */}
      <View
        variants={['bg:neutral1', 'padding:2', 'center']}
        style={{ width: '100%', borderRadius: 24, flexDirection: 'column' }}
      >
        <Text text={message.title} variants={['h4', 'textCenter', 'color:neutral10']} />
        {message.body ? (
          <Text text={message.body} variants={['p2', 'textCenter', 'color:neutral10', 'marginTop:1']} />
        ) : null}
        {message.retryable ? (
          <Text text="Tap to retry" variants={['p3', 'textCenter', 'color:neutral6', 'marginTop:2']} />
        ) : null}
      </View>
    </View>
  )
}
